'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface CalculatorResult {
  monthlySavings: number;
  annualSavings: number;
  roi: number;
  paybackPeriod: number;
}

const ROICalculator: React.FC = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [industry, setIndustry] = useState<string>('Healthcare');
  const [employees, setEmployees] = useState<string>('50');
  const [currentITSpend, setCurrentITSpend] = useState<string>('10000');
  const [downtimeHours, setDowntimeHours] = useState<string>('5');
  const [result, setResult] = useState<CalculatorResult | null>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const calculateROI = () => {
    const empCount = parseInt(employees) || 0;
    const itSpend = parseInt(currentITSpend) || 0;
    const downtime = parseInt(downtimeHours) || 0;

    const industryMultiplier: Record<string, number> = {
      Healthcare: 0.25,
      Finance: 0.30,
      Manufacturing: 0.20,
      Retail: 0.22,
      Education: 0.18,
      'Small Business': 0.28,
    };

    const multiplier = industryMultiplier[industry] || 0.25;
    const downtimeCost = downtime * empCount * 50;
    const monthlySavings = itSpend * multiplier + downtimeCost * 0.8;
    const annualSavings = monthlySavings * 12;
    const investmentCost = itSpend * 2;
    const roi = ((annualSavings - investmentCost) / investmentCost) * 100;
    const paybackPeriod = investmentCost / monthlySavings;

    setResult({
      monthlySavings: Math.round(monthlySavings),
      annualSavings: Math.round(annualSavings),
      roi: Math.round(roi),
      paybackPeriod: Math.round(paybackPeriod * 10) / 10,
    });
  };

  if (!isHydrated) {
    return (
      <section className="py-16 bg-muted">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-card rounded-xl shadow-card p-8">
            <div className="animate-pulse space-y-4">
              <div className="h-8 bg-muted rounded w-3/4 mx-auto"></div>
              <div className="h-4 bg-muted rounded w-1/2 mx-auto"></div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 bg-muted">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-conversion-accent/10 rounded-full mb-4">
              <Icon name="CalculatorIcon" size={20} className="text-conversion-accent" variant="solid" />
              <span className="text-sm font-cta font-semibold text-conversion-accent">ROI Calculator</span>
            </div>
            <h2 className="text-3xl lg:text-4xl font-headline font-bold text-primary mb-4">
              Calculate Your Potential Savings
            </h2>
            <p className="text-lg text-muted-foreground font-body">
              See how much your organization could save with our industry-specific IT solutions
            </p>
          </div>

          <div className="bg-card rounded-xl shadow-card p-8">
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div>
                <label className="block text-sm font-cta font-semibold text-primary mb-2">Industry</label>
                <select
                  value={industry}
                  onChange={(e) => setIndustry(e.target.value)}
                  className="w-full px-4 py-3 bg-background border border-border rounded-lg font-body text-foreground focus:outline-none focus:ring-2 focus:ring-brand-primary"
                >
                  <option value="Healthcare">Healthcare</option>
                  <option value="Finance">Finance</option>
                  <option value="Manufacturing">Manufacturing</option>
                  <option value="Retail">Retail</option>
                  <option value="Education">Education</option>
                  <option value="Small Business">Small Business</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-cta font-semibold text-primary mb-2">Number of Employees</label>
                <input
                  type="number"
                  value={employees}
                  onChange={(e) => setEmployees(e.target.value)}
                  className="w-full px-4 py-3 bg-background border border-border rounded-lg font-body text-foreground focus:outline-none focus:ring-2 focus:ring-brand-primary"
                  placeholder="50"
                />
              </div>

              <div>
                <label className="block text-sm font-cta font-semibold text-primary mb-2">
                  Current Monthly IT Spend ($)
                </label>
                <input
                  type="number"
                  value={currentITSpend}
                  onChange={(e) => setCurrentITSpend(e.target.value)}
                  className="w-full px-4 py-3 bg-background border border-border rounded-lg font-body text-foreground focus:outline-none focus:ring-2 focus:ring-brand-primary"
                  placeholder="10000"
                />
              </div>

              <div>
                <label className="block text-sm font-cta font-semibold text-primary mb-2">
                  Monthly Downtime Hours
                </label>
                <input
                  type="number"
                  value={downtimeHours}
                  onChange={(e) => setDowntimeHours(e.target.value)}
                  className="w-full px-4 py-3 bg-background border border-border rounded-lg font-body text-foreground focus:outline-none focus:ring-2 focus:ring-brand-primary"
                  placeholder="5"
                />
              </div>
            </div>

            <button
              onClick={calculateROI}
              className="w-full flex items-center justify-center space-x-2 px-6 py-4 bg-brand-conversion hover:bg-brand-conversion/90 text-white rounded-lg font-cta font-semibold text-lg shadow-card transition-all duration-300 transform hover:scale-105"
            >
              <Icon name="ChartBarIcon" size={24} variant="solid" />
              <span>Calculate ROI</span>
            </button>

            {result && (
              <div className="mt-8 grid md:grid-cols-2 gap-6">
                <div className="bg-gradient-to-br from-success/10 to-success/5 rounded-xl p-6 border border-success/20">
                  <div className="flex items-center space-x-3 mb-3">
                    <Icon name="CurrencyDollarIcon" size={24} className="text-success" variant="solid" />
                    <h3 className="text-lg font-headline font-bold text-primary">Monthly Savings</h3>
                  </div>
                  <div className="text-3xl font-headline font-bold text-success">
                    ${result.monthlySavings.toLocaleString()}
                  </div>
                </div>

                <div className="bg-gradient-to-br from-brand-primary/10 to-brand-primary/5 rounded-xl p-6 border border-brand-primary/20">
                  <div className="flex items-center space-x-3 mb-3">
                    <Icon name="TrendingUpIcon" size={24} className="text-brand-primary" variant="solid" />
                    <h3 className="text-lg font-headline font-bold text-primary">Annual Savings</h3>
                  </div>
                  <div className="text-3xl font-headline font-bold text-brand-primary">
                    ${result.annualSavings.toLocaleString()}
                  </div>
                </div>

                <div className="bg-gradient-to-br from-conversion-accent/10 to-conversion-accent/5 rounded-xl p-6 border border-conversion-accent/20">
                  <div className="flex items-center space-x-3 mb-3">
                    <Icon name="ChartPieIcon" size={24} className="text-conversion-accent" variant="solid" />
                    <h3 className="text-lg font-headline font-bold text-primary">ROI Percentage</h3>
                  </div>
                  <div className="text-3xl font-headline font-bold text-conversion-accent">{result.roi}%</div>
                </div>

                <div className="bg-gradient-to-br from-accent/10 to-accent/5 rounded-xl p-6 border border-accent/20">
                  <div className="flex items-center space-x-3 mb-3">
                    <Icon name="ClockIcon" size={24} className="text-accent" variant="solid" />
                    <h3 className="text-lg font-headline font-bold text-primary">Payback Period</h3>
                  </div>
                  <div className="text-3xl font-headline font-bold text-accent">
                    {result.paybackPeriod} months
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ROICalculator;